/**
 * Email template system for audit tracker.
 * Supports client-saved templates (with placeholders) and built-in defaults.
 *
 * Placeholders: {{property_name}}, {{contact_name}}, {{year}}, {{month}},
 *   {{files_required}}, {{files_received}}, {{files_remaining}}, {{state}}
 */

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Available placeholders for the template editor UI
export const PLACEHOLDERS = [
  { key: '{{property_name}}', label: 'Property Name' },
  { key: '{{contact_name}}', label: 'Contact Name' },
  { key: '{{year}}', label: 'Year' },
  { key: '{{month}}', label: 'Audit Month' },
  { key: '{{files_required}}', label: 'Files Required' },
  { key: '{{files_received}}', label: 'Files Received' },
  { key: '{{files_remaining}}', label: 'Files Remaining' },
  { key: '{{state}}', label: 'State' },
];

// Status groups that share the same template key
const STATUS_TEMPLATE_KEY = {
  scheduled: 'scheduled',
  setup_requested: 'scheduled',
  files_selected: 'files_requested',
  files_requested: 'files_requested',
  partially_received: 'partially_received',
  all_received: 'in_review',
  in_review: 'in_review',
  review_complete: 'completed',
  completed: 'completed',
};

// All template keys with labels (for the settings UI)
export const TEMPLATE_STATUSES = [
  { key: 'scheduled', label: 'Upcoming Audit / Setup' },
  { key: 'files_requested', label: 'File Request' },
  { key: 'partially_received', label: 'Outstanding Files' },
  { key: 'in_review', label: 'Review Update' },
  { key: 'completed', label: 'Review Complete' },
  { key: 'default', label: 'General / Other' },
];

// ── Built-in defaults ────────────────────────────────────────────

const DEFAULTS = {
  scheduled: {
    subject: 'Upcoming {{year}} Annual File Review - {{property_name}}',
    body: `Hi {{contact_name}},

This is to inform you that {{property_name}} has been selected for the {{year}} annual file review{{month}}.

We will be in touch shortly with details on what files will be needed. In the meantime, please ensure your tenant files and certifications are up to date.

Please let us know if you have any questions.

Thank you,`,
  },
  files_requested: {
    subject: 'File Request - {{property_name}} - {{year}} Annual Review',
    body: `Hi {{contact_name}},

As part of the {{year}} annual file review for {{property_name}}, we need the following tenant files submitted for review ({{files_required}} files total).

Please upload the requested files at your earliest convenience. If you have any questions about which files are needed, please don't hesitate to reach out.

Thank you for your prompt attention to this matter.

Best regards,`,
  },
  partially_received: {
    subject: 'Outstanding Files Needed - {{property_name}} - {{year}} Annual Review',
    body: `Hi {{contact_name}},

Thank you for the files submitted so far for {{property_name}}. We have received {{files_received}} of {{files_required}} files.

We still need {{files_remaining}} more file(s) to complete the review. Could you please submit the outstanding files at your earliest convenience?

Please let us know if you need any clarification on which files are still outstanding.

Thank you,`,
  },
  in_review: {
    subject: 'Review Update - {{property_name}} - {{year}} Annual Review',
    body: `Hi {{contact_name}},

This is to let you know that all files for {{property_name}} have been received and are currently under review.

We will notify you if any corrections are needed or when the review is complete.

Thank you,`,
  },
  completed: {
    subject: 'Review Complete - {{property_name}} - {{year}} Annual Review',
    body: `Hi {{contact_name}},

The {{year}} annual file review for {{property_name}} has been completed. The final report is available for your records.

Thank you for your cooperation throughout the review process.

Best regards,`,
  },
  default: {
    subject: '{{year}} Annual File Review - {{property_name}}',
    body: `Hi {{contact_name}},

Regarding the {{year}} annual file review for {{property_name}} —


Thank you,`,
  },
};

// ── Placeholder replacement ──────────────────────────────────────

function replacePlaceholders(text, property, contact) {
  if (!text) return '';
  const year = new Date().getFullYear();
  const month = property.audit_month ? ` scheduled for ${MONTHS[property.audit_month]}` : '';
  const filesRemaining = Math.max(0, (property.files_required || 0) - (property.files_received || 0));

  return text
    .replace(/\{\{property_name\}\}/g, property.property_name || 'Property')
    .replace(/\{\{contact_name\}\}/g, contact?.name?.split(' ')[0] || 'Property Manager')
    .replace(/\{\{year\}\}/g, String(year))
    .replace(/\{\{month\}\}/g, month)
    .replace(/\{\{files_required\}\}/g, String(property.files_required || 0))
    .replace(/\{\{files_received\}\}/g, String(property.files_received || 0))
    .replace(/\{\{files_remaining\}\}/g, String(filesRemaining))
    .replace(/\{\{state\}\}/g, property.state || '');
}

// ── Public API ───────────────────────────────────────────────────

/**
 * Get the resolved template for a property + contact.
 * Uses client templates if provided, otherwise falls back to defaults.
 * Returns { subject, body } with placeholders replaced.
 */
export function getEmailTemplate(property, contact, clientTemplates = {}) {
  const status = property.status || 'scheduled';
  const templateKey = STATUS_TEMPLATE_KEY[status] || 'default';

  // Try client template first, then default
  const clientTpl = clientTemplates[templateKey];
  const defaultTpl = DEFAULTS[templateKey] || DEFAULTS.default;

  const subject = (clientTpl?.subject_template || defaultTpl.subject);
  const body = (clientTpl?.body_template || defaultTpl.body);

  return {
    subject: replacePlaceholders(subject, property, contact),
    body: replacePlaceholders(body, property, contact),
    templateKey,
  };
}

/**
 * Get the raw template (with placeholders intact) for editing in settings.
 */
export function getRawTemplate(templateKey, clientTemplates = {}) {
  const clientTpl = clientTemplates[templateKey];
  const defaultTpl = DEFAULTS[templateKey] || DEFAULTS.default;
  return {
    subject_template: clientTpl?.subject_template || defaultTpl.subject,
    body_template: clientTpl?.body_template || defaultTpl.body,
  };
}

/**
 * Open the user's email client with the resolved template.
 */
export function openAuditEmail(property, contact, clientTemplates = {}) {
  if (!contact?.email) return;
  const { subject, body } = getEmailTemplate(property, contact, clientTemplates);
  const mailto = `mailto:${encodeURIComponent(contact.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.open(mailto, '_blank');
}

/**
 * Open mailto with custom (already-edited) subject and body.
 */
export function openMailto(email, subject, body) {
  if (!email) return;
  const mailto = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.open(mailto, '_blank');
}

export default openAuditEmail;
